import React, { ReactNode, useImperativeHandle, useRef } from 'react';
import { ElementContainer } from '../../grid/ElementContainer/ElementContainer';
import { Label } from '../Label/Label';
import { FieldWrapper } from '../../grid/FieldWrapper/FieldWrapper';
import { TextParam } from '../../types/TextParam';
import { useHtmlValidId } from '../../../../hooks/useHtmlValidId';
import { NumberParam } from '../../types/NumberParam';
import { UrlParam } from '../../types/UrlParam';
import { useSonTranslation } from '../../../../translation/useSonTranslation';
import { useValidateTextLength } from '../../utils/useValidateTextLength';
import { DateParam } from '../../types/DateParam';
import { isNullOrUndefined } from '../../../../lib/typeGuards';
import { TelParam } from '../../types/TelParam';

type HTMLInputProps = React.DetailedHTMLProps<React.InputHTMLAttributes<HTMLInputElement>, HTMLInputElement>;
type ValidInputParams = TextParam<string> | NumberParam<string> | UrlParam<string> | DateParam<string>
  | TelParam<string>;

type Input = {
  label: ReactNode;
  helpText?: string;
  helpTextTooltipPlacement?: Label['tooltipPlacement'];
  helpTextPlacement?: Label['helpTextPlacement'];
  param?: ValidInputParams;
  ref?: React.RefObject<HTMLInputElement | null>;
} & Omit<HTMLInputProps, 'id' | 'ref' | 'className'>;

function normalizeNumberParam(
  param: NumberParam<string>,
  inputProps: Omit<HTMLInputProps, 'id' | 'ref' | 'className'>,
) {
  return {
    type: param?.type,
    defaultValue: inputProps.value ? undefined : param?.value,
    required: param?.required,
    min: param?.constraints?.min,
    max: param?.constraints?.max,
    name: param?.name,
    ...inputProps,
  };
}

function normalizeDateParam(param: DateParam<string>, inputProps: Omit<HTMLInputProps, 'id' | 'ref' | 'className'>) {
  return {
    type: param?.type === 'datetime' ? 'datetime-local' : param?.type,
    defaultValue: inputProps.value ? undefined : param?.value,
    required: param?.required,
    min: param?.constraints?.min || param?.constraints?.minDate,
    max: param?.constraints?.max || param?.constraints?.maxDate,
    name: param?.name,
    ...inputProps,
  };
}

/**
 * Normalize constraints and values from backend, which are not html conform,
 * to html conform input values.
 */
function normalizeParamsToHTMLAttributes(inputProps: HTMLInputProps, param?: ValidInputParams) {
  switch (param?.type) {
    case 'number':
      return normalizeNumberParam(param, inputProps);
    case 'time':
    case 'datetime-local':
    case 'date':
    case 'datetime':
      return normalizeDateParam(param, inputProps);
    default:
      return {
        type: param?.type,
        defaultValue: inputProps.value ? undefined : param?.value,
        required: param?.required,
        maxLength: param?.constraints?.maxLength,
        readOnly: param?.constraints?.readonly,
        pattern: param?.constraints?.pattern,
        name: param?.name,
        ...inputProps,
      };
  }
}

export function Input({
  label,
  helpText,
  helpTextTooltipPlacement,
  helpTextPlacement,
  param,
  ref,
  ...inputProps
}: Input) {
  const { t } = useSonTranslation('Form');

  const inputRef = useRef<HTMLInputElement>(null);
  // expose the inputRef from outside
  useImperativeHandle(ref, () => inputRef.current as HTMLInputElement);

  const id = useHtmlValidId();

  const helpTextId = `input-help-text-${id}`;
  const inputId = `input-${id}`;

  const htmlAttributes = normalizeParamsToHTMLAttributes(inputProps, param);

  useValidateTextLength(inputRef);

  // fields can not! only contain white spaces.
  // check for >= 1 for normal validation of fields
  const validateEmptySpaces = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { type, pattern } = htmlAttributes;
    if (type === 'text' && isNullOrUndefined(pattern) && e.target.value.length >= 1 && e.target.value.trim() === '') {
      e.target.setCustomValidity(t('fieldCantBeEmpty'));
    } else {
      e.target.setCustomValidity('');
    }
  };

  return (
    <ElementContainer>
      <Label
        htmlFor={inputId}
        helpText={helpText}
        helpTextId={helpTextId}
        tooltipPlacement={helpTextTooltipPlacement}
        helpTextPlacement={helpTextPlacement}
        required={htmlAttributes?.required}
      >
        {label}
      </Label>
      <FieldWrapper
        // no inputCounter on readonly inputs
        maxLength={htmlAttributes.readOnly ? undefined : htmlAttributes.maxLength}
        elementRef={inputRef}
      >
        <input
          {...htmlAttributes}
          ref={inputRef}
          id={inputId}
          aria-describedby={helpText && helpTextId}
          className="form2-input"
          onChange={(e) => {
            validateEmptySpaces(e);

            if (htmlAttributes.onChange) {
              htmlAttributes.onChange(e);
            }
          }}
        />
      </FieldWrapper>
    </ElementContainer>
  );
}
